import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import { ChevronRight, History, ShieldAlert } from 'lucide-react'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/useAuth'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { StateBadge } from '@/components/state-badge'
import { StepUpButton } from '@/components/StepUpButton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import type { PackageLifecycleState, TransitionRow } from '@/lib/types/lifecycle'
import { EcosystemMark } from '@/components/hawk-primitives'

interface LifecycleEvent {
  version_id: string
  ecosystem: string
  repository_name: string
  package_name: string
  version: string
  state: PackageLifecycleState
  state_reason: string | null
  state_changed_at: string
  ever_quarantined: boolean
  last_quarantine_reason: string | null
}

export const Route = createFileRoute('/_app/org/security/')({
  component: SecurityPage,
})

function SecurityPage() {
  const { currentOrgSlug } = useAuth()
  const [search, setSearch] = useState('')
  const [history, setHistory] = useState<LifecycleEvent | null>(null)
  const [pending, setPending] = useState<{
    event: LifecycleEvent
    to: PackageLifecycleState
  } | null>(null)

  const events = useOrgQuery<LifecycleEvent[]>(
    ['admin-lifecycle-events'],
    '/admin/lifecycle/events',
    { refetchInterval: 30_000 },
  )

  const filtered = useMemo(() => {
    const all = events.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (e) =>
        e.package_name.toLowerCase().includes(q) ||
        e.version.toLowerCase().includes(q) ||
        e.repository_name.toLowerCase().includes(q),
    )
  }, [events.data, search])

  const sections = useMemo(() => {
    const decidedStates: PackageLifecycleState[] = [
      'blacklisted',
      'deprecated',
      'deleted',
      'purged',
    ]
    return {
      needsDecision: filtered.filter((e) => e.state === 'quarantined'),
      decided: filtered.filter((e) => decidedStates.includes(e.state)),
      history: filtered.filter(
        (e) => e.state === 'live' && e.ever_quarantined,
      ),
    }
  }, [filtered])

  const totalRows =
    sections.needsDecision.length +
    sections.decided.length +
    sections.history.length

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Security"
        description="Versions in non-default lifecycle states and packages with quarantine history."
      />

      <div className="grid grid-cols-3 border border-border rounded-md bg-card/40 overflow-hidden mt-8 mb-8">
        <Tile label="Quarantined" value={sections.needsDecision.length} tone="amber" />
        <Tile label="Decided" value={sections.decided.length} tone="destructive" />
        <Tile label="Formerly Quarantined" value={sections.history.length} tone="muted" />
      </div>

      <div className="mb-6">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="package, version, repository..."
          className="max-w-md bg-secondary border-transparent font-mono text-xs"
        />
      </div>

      {events.isLoading ? (
        <div className="border border-border bg-card/30 rounded-md h-[240px] hawk-shimmer" />
      ) : totalRows === 0 ? (
        <div className="border border-border border-dashed rounded-md py-16 text-center bg-card/30">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            All clear
          </div>
          <p className="text-sm text-muted-foreground">
            No versions need security attention and no quarantine history matches.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          <Section
            label="Needs decision"
            sublabel="Automatically quarantined, awaiting an admin choice."
            tone="amber"
            events={sections.needsDecision}
            onPick={(e, to) => setPending({ event: e, to })}
            onHistory={(e) => setHistory(e)}
          />
          <Section
            label="Decided"
            sublabel="Admin-driven states. Listed for visibility; no action required."
            tone="muted"
            events={sections.decided}
            onPick={(e, to) => setPending({ event: e, to })}
            onHistory={(e) => setHistory(e)}
          />
          <Section
            label="History"
            sublabel="Currently Live versions that were previously quarantined."
            tone="muted"
            events={sections.history}
            onPick={(e, to) => setPending({ event: e, to })}
            onHistory={(e) => setHistory(e)}
          />
        </div>
      )}

      {history && (
        <HistoryDialog
          event={history}
          onClose={() => setHistory(null)}
        />
      )}
      {pending && (
        <PendingTransitionDialog
          event={pending.event}
          to={pending.to}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  )
}

function Section({
  label,
  sublabel,
  tone,
  events,
  onPick,
  onHistory,
}: {
  label: string
  sublabel: string
  tone: 'amber' | 'muted'
  events: LifecycleEvent[]
  onPick: (event: LifecycleEvent, to: PackageLifecycleState) => void
  onHistory: (event: LifecycleEvent) => void
}) {
  if (events.length === 0) return null
  const accent =
    tone === 'amber'
      ? 'text-amber-500 ring-amber-500/30'
      : 'text-muted-foreground ring-muted-foreground/20'
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-3 pb-3 border-b border-border">
        <h2 className={`font-mono text-[11px] uppercase tracking-[0.22em] ${accent.split(' ')[0]}`}>
          {label}
        </h2>
        <span
          className={`font-mono text-[10px] tabular-nums px-1.5 py-0.5 rounded-sm ring-1 ring-inset ${accent}`}
        >
          {events.length}
        </span>
        <p className="text-xs text-muted-foreground flex-1 truncate">
          {sublabel}
        </p>
      </div>
      <ul className="border border-border rounded-md bg-card divide-y divide-border">
        {events.map((e) => (
          <EventRow
            key={e.version_id}
            event={e}
            onPick={(to) => onPick(e, to)}
            onHistory={() => onHistory(e)}
          />
        ))}
      </ul>
    </section>
  )
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'amber' | 'destructive' | 'muted'
}) {
  const color =
    tone === 'amber'
      ? 'text-amber-500'
      : tone === 'destructive'
        ? 'text-destructive'
        : 'text-muted-foreground'
  return (
    <div className="px-6 py-5 border-r border-border last:border-r-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
        {label}
      </div>
      <div className={`font-mono text-3xl tabular-nums ${color}`}>{value}</div>
    </div>
  )
}

function EventRow({
  event,
  onPick,
  onHistory,
}: {
  event: LifecycleEvent
  onPick: (to: PackageLifecycleState) => void
  onHistory: () => void
}) {
  return (
    <li className="grid grid-cols-[44px_1fr_auto_auto_auto] gap-3 items-center px-5 py-3 hover:bg-popover transition-colors">
      <EcosystemMark ecosystem={event.ecosystem} size="sm" />
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <Link
            to="/packages/$repo"
            params={{ repo: event.repository_name }}
            search={{ q: `${event.package_name}@${event.version}` }}
            className="text-sm font-medium hover:text-primary truncate"
          >
            {event.package_name}
            <span className="text-muted-foreground/60">{' / '}</span>
            <span className="text-foreground/70">{event.version}</span>
          </Link>
          <StateBadge state={event.state} size="xs" />
          {event.state !== 'quarantined' && event.ever_quarantined && (
            <span
              title={
                event.last_quarantine_reason ??
                'This version was quarantined at some point'
              }
              className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.18em] ring-1 ring-inset ring-amber-500/40 text-amber-500 bg-amber-500/10 rounded-sm px-1.5 py-0.5"
            >
              <ShieldAlert className="size-2.5" strokeWidth={1.75} />
              formerly quarantined
            </span>
          )}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground truncate">
          {event.state_reason ?? event.last_quarantine_reason ?? '—'}
        </div>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums">
        {event.repository_name}
      </div>
      <div
        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums"
        title={new Date(event.state_changed_at).toLocaleString()}
      >
        {formatDate(event.state_changed_at)}
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onHistory}
          className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 px-2"
          title="View transition history"
        >
          <History className="size-3.5" strokeWidth={1.75} />
        </Button>
        <ActionPicker event={event} onPick={onPick} />
      </div>
    </li>
  )
}

function ActionPicker({
  event,
  onPick,
}: {
  event: LifecycleEvent
  onPick: (to: PackageLifecycleState) => void
}) {
  const choices: PackageLifecycleState[] = availableTransitions(event.state)
  if (choices.length === 0) return null
  return (
    <Select
      value=""
      onValueChange={(v) => onPick(v as PackageLifecycleState)}
    >
      <SelectTrigger className="h-8 text-xs w-36 gap-1.5 bg-primary/10 hover:bg-primary/15 border-primary/30 hover:border-primary/60 text-primary font-medium transition-colors">
        <SelectValue placeholder="Transition" />
      </SelectTrigger>
      <SelectContent>
        {choices.map((to) => (
          <SelectItem key={to} value={to}>
            {transitionLabel(to)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function availableTransitions(from: PackageLifecycleState): PackageLifecycleState[] {
  switch (from) {
    case 'staging':
      return ['live', 'quarantined', 'deprecated', 'blacklisted', 'deleted']
    case 'live':
      return ['quarantined', 'deprecated', 'blacklisted', 'deleted']
    case 'quarantined':
      return ['live', 'deprecated', 'blacklisted', 'deleted']
    case 'deprecated':
      return ['live', 'quarantined', 'blacklisted', 'deleted']
    case 'blacklisted':
      return ['staging', 'live', 'quarantined', 'deprecated', 'deleted']
    case 'deleted':
    case 'purged':
      return ['staging', 'live']
  }
}

function transitionLabel(to: PackageLifecycleState): string {
  switch (to) {
    case 'live': return 'Promote to Live'
    case 'staging': return 'Demote to Staging'
    case 'quarantined': return 'Quarantine'
    case 'deprecated': return 'Deprecate'
    case 'blacklisted': return 'Blacklist'
    case 'deleted': return 'Delete'
    case 'purged': return 'Purge'
  }
}

function stepUpScope(from: PackageLifecycleState, to: PackageLifecycleState): string {
  if (from === 'blacklisted') return 'package.unblacklist'
  if (from === 'deleted' || from === 'purged') return 'package.undelete'
  if (to === 'deleted' || to === 'purged') return 'package.delete'
  if (to === 'staging' || to === 'live') return 'package.promote'
  if (to === 'quarantined') return 'package.quarantine'
  if (to === 'deprecated') return 'package.deprecate'
  return 'package.blacklist'
}

function PendingTransitionDialog({
  event,
  to,
  onClose,
}: {
  event: LifecycleEvent
  to: PackageLifecycleState
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { currentOrgSlug } = useAuth()
  const [reason, setReason] = useState('')
  const scope = stepUpScope(event.state, to)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {transitionLabel(to)} {event.package_name} {event.version}
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono uppercase">{event.state}</span>
            {' → '}
            <span className="font-mono uppercase">{to}</span>. The reason is
            written to the transition log.
          </DialogDescription>
        </DialogHeader>
        <div className="py-3">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <StepUpButton
            scope={scope}
            onAction={async () => {
              const r = await apiFetch(
                orgPath(`/admin/repositories/${encodeURIComponent(event.repository_name)}/packages/${encodeURIComponent(event.package_name)}/versions/${encodeURIComponent(event.version)}/state`),
                {
                  method: 'POST',
                  body: JSON.stringify({ to, reason: reason || null }),
                },
              )
              if (!r.ok) throw new Error(await r.text())
            }}
            onSuccess={() => {
              toast.success(`${event.package_name} ${event.version}: ${transitionLabel(to)}`)
              queryClient.invalidateQueries({ queryKey: ['admin-lifecycle-events', currentOrgSlug] })
              onClose()
            }}
          >
            {transitionLabel(to)}
          </StepUpButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function HistoryDialog({
  event,
  onClose,
}: {
  event: LifecycleEvent
  onClose: () => void
}) {
  const q = useOrgQuery<TransitionRow[]>(
    ['transitions', event.repository_name, event.package_name, event.version],
    `/admin/repositories/${encodeURIComponent(event.repository_name)}/packages/${encodeURIComponent(event.package_name)}/versions/${encodeURIComponent(event.version)}/transitions`,
  )
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Transition history
          </div>
          <DialogTitle className="font-mono text-base tracking-tight">
            {event.package_name}
            <span className="text-muted-foreground/60">{' / '}</span>
            <span className="text-primary">{event.version}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {q.isLoading && (
            <div className="text-muted-foreground font-mono text-[11px] uppercase tracking-[0.18em] py-10 text-center">
              Loading
            </div>
          )}
          {q.data && q.data.length === 0 && (
            <div className="text-muted-foreground font-mono text-[11px] uppercase tracking-[0.18em] py-10 text-center">
              No transitions recorded
            </div>
          )}
          {q.data && q.data.length > 0 && (
            <ul className="divide-y divide-border">
              {q.data.map((t) => (
                <li key={t.id} className="py-3">
                  <div className="flex items-center gap-2 mb-1">
                    {t.from_state ? (
                      <StateBadge state={t.from_state} size="xs" />
                    ) : (
                      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/60 px-1.5">
                        initial
                      </span>
                    )}
                    <ChevronRight className="size-3 text-muted-foreground/50" strokeWidth={1.75} />
                    <StateBadge state={t.to_state} size="xs" />
                    <span
                      className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums"
                      title={new Date(t.created_at).toLocaleString()}
                    >
                      {formatDate(t.created_at)}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                    {t.triggered_by}
                  </div>
                  {t.reason && (
                    <div className="text-[13px] text-foreground/85 mt-1.5">
                      {t.reason}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
